import React, { useState } from 'react';
import {
  Box, Typography, Button, Stack, TextField, IconButton,
} from '@mui/material';
import { Plus, Trash2, Pencil } from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';

const navy = '#1e3a5f';

const inputSx = { '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e5e7eb' } };

const EMPTY_ITEM = () => ({ id: uuidv4(), label: '', value: '', color: navy });

/**
 * FinancialItemsStep
 * הגדרת פריטי הכנסה או הוצאה (FinancialGaugeWidget)
 * נעשה בו שימוש כפול — פעם להכנסות ופעם להוצאות
 *
 * Props:
 *   field         string  — 'incomeData' | 'expensesData'
 *   sectionLabel  string  — 'הכנסות' | 'הוצאות'
 *   color         string  — default color for new items
 *   formData      object
 *   onChange      (partial) => void
 */
const FinancialItemsStep = ({ field = 'incomeData', sectionLabel = 'פריטים', color = navy, formData, onChange }) => {
  const items = formData?.[field] || [];
  const [draft, setDraft] = useState(null);
  const [editingIndex, setEditingIndex] = useState(null);

  const isOpen = draft !== null;

  const updateItems = (updated) => onChange({ [field]: updated });
  const removeItem = (index) => {
    updateItems(items.filter((_, i) => i !== index));
    if (editingIndex === index) closeDraft();
  };

  const openAdd = () => {
    setEditingIndex(null);
    setDraft({ ...EMPTY_ITEM(), color });
  };

  const startEdit = (index) => {
    setEditingIndex(index);
    setDraft({ ...items[index] });
  };

  const closeDraft = () => {
    setDraft(null);
    setEditingIndex(null);
  };

  const saveDraft = () => {
    if (!draft.label.trim()) return;
    const cleaned = { ...draft, value: Number(draft.value) || 0 };
    if (editingIndex !== null) {
      updateItems(items.map((item, i) => i === editingIndex ? cleaned : item));
    } else {
      updateItems([...items, cleaned]);
    }
    closeDraft();
  };

  return (
    <Box dir="rtl">
      <Typography sx={{ fontWeight: 700, color: navy, fontSize: '1rem', mb: 0.5 }}>
        {sectionLabel}
      </Typography>
      <Typography sx={{ fontSize: '0.8rem', color: '#6b7280', mb: 2.5, lineHeight: 1.5 }}>
        הוסף פריטים עם תווית, סכום וצבע לתצוגה בגרף.
      </Typography>

      <Box sx={{ display: 'flex', gap: 4, alignItems: 'flex-start' }}>
        {/* Right side (RTL): list */}
        <Box sx={{ flex: isOpen ? '0 0 40%' : 1, minWidth: 0, transition: 'flex 0.2s' }}>
          <Button
            startIcon={<Plus size={15} />}
            onClick={openAdd}
            disabled={isOpen}
            sx={{
              border: '1.5px dashed #d1d5db',
              borderRadius: '10px', color: '#6b7280', textTransform: 'none',
              fontSize: '0.8rem', px: 2, py: 0.75, width: '100%', mb: 1.5,
              '&:hover': { borderColor: navy, color: navy, bgcolor: '#f0f4fa' },
              '&.Mui-disabled': { borderColor: '#e5e7eb', color: '#c0c0c0' },
            }}
          >
            הוסף פריט
          </Button>

          {items.length > 0 ? (
            <Box sx={{ border: '1px solid #e5e7eb', borderRadius: 2, overflowY: 'auto', maxHeight: 'calc(70vh - 160px)' }}>
              {items.map((item, i) => {
                const isActive = editingIndex === i;
                return (
                  <Box key={item.id || i} sx={{
                    px: 1.5, py: 1,
                    display: 'flex', alignItems: 'center', gap: 0.75,
                    bgcolor: isActive ? '#eff6ff' : i % 2 === 0 ? '#fafafa' : 'white',
                    borderBottom: i < items.length - 1 ? '1px solid #f0f0f0' : 'none',
                    borderRight: isActive ? `3px solid ${navy}` : '3px solid transparent',
                    transition: 'all 0.15s',
                  }}>
                    <Box sx={{ width: 14, height: 14, borderRadius: '50%', bgcolor: item.color, border: '1px solid rgba(0,0,0,0.08)', flexShrink: 0 }} />
                    <Stack sx={{ flex: 1, minWidth: 0 }}>
                      <Typography noWrap sx={{ fontWeight: 600, fontSize: '0.82rem', color: '#111827' }}>
                        {item.label}
                      </Typography>
                      <Typography noWrap sx={{ fontSize: '0.72rem', color: '#6b7280' }}>
                        ₪{Number(item.value).toLocaleString()}
                      </Typography>
                    </Stack>
                    <IconButton size="small" onClick={() => startEdit(i)}
                      sx={{ color: isActive ? navy : '#9ca3af', '&:hover': { color: navy }, p: 0.5 }}>
                      <Pencil size={13} />
                    </IconButton>
                    <IconButton size="small" onClick={() => removeItem(i)}
                      sx={{ color: '#9ca3af', '&:hover': { color: '#ef4444' }, p: 0.5 }}>
                      <Trash2 size={13} />
                    </IconButton>
                  </Box>
                );
              })}
              {/* Total */}
              <Box sx={{ px: 1.5, py: 0.75, bgcolor: '#f8fafc', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between' }}>
                <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: '#374151' }}>סה"כ</Typography>
                <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: navy }}>
                  ₪{items.reduce((sum, i) => sum + (Number(i.value) || 0), 0).toLocaleString()}
                </Typography>
              </Box>
            </Box>
          ) : !isOpen && (
            <Typography sx={{ textAlign: 'center', fontSize: '0.78rem', color: '#9ca3af', mt: 1 }}>
              אין פריטים עדיין
            </Typography>
          )}
        </Box>

        {/* Left side (RTL): form */}
        {isOpen && (
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box key={editingIndex ?? 'new'} sx={{ border: '1px solid #bae6fd', borderRadius: 2, bgcolor: '#f0f9ff', p: 2, mb: 1.5 }}>
              <Stack spacing={1.5}>
                <Stack direction="row" spacing={1.5}>
                  <TextField
                    size="small"
                    label="תווית"
                    placeholder="תחזוקה, משכורות..."
                    value={draft.label}
                    onChange={(e) => setDraft(d => ({ ...d, label: e.target.value }))}
                    sx={{ flex: 1, ...inputSx, bgcolor: 'white' }}
                  />
                  <TextField
                    size="small"
                    label="סכום (₪)"
                    type="number"
                    placeholder="5000"
                    value={draft.value}
                    onChange={(e) => setDraft(d => ({ ...d, value: e.target.value }))}
                    inputProps={{ min: 0 }}
                    sx={{ width: 110, flexShrink: 0, ...inputSx, bgcolor: 'white' }}
                  />
                </Stack>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Typography sx={{ fontSize: '0.82rem', color: '#374151' }}>צבע:</Typography>
                  <Box
                    component="input"
                    type="color"
                    value={draft.color}
                    onChange={(e) => setDraft(d => ({ ...d, color: e.target.value }))}
                    style={{ width: 40, height: 34, border: '1px solid #e5e7eb', borderRadius: 6, cursor: 'pointer', padding: 2 }}
                  />
                  <Typography sx={{ fontSize: '0.75rem', color: '#9ca3af' }}>{draft.color}</Typography>
                </Stack>
                <Stack direction="row" spacing={1} justifyContent="flex-end">
                  <Button onClick={closeDraft} sx={{ color: '#6b7280', textTransform: 'none', fontSize: '0.8rem' }}>
                    ביטול
                  </Button>
                  <Button
                    variant="contained"
                    onClick={saveDraft}
                    disabled={!draft.label.trim()}
                    sx={{
                      bgcolor: navy, color: 'white', textTransform: 'none', fontSize: '0.8rem',
                      boxShadow: 'none', borderRadius: '8px',
                      '&:hover': { bgcolor: '#152844', boxShadow: 'none' },
                    }}
                  >
                    {editingIndex !== null ? 'עדכן' : 'הוסף'}
                  </Button>
                </Stack>
              </Stack>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default FinancialItemsStep;
